/**
 * Sign-in, one per tool (Design's sketch "the split", Nino's go 27 Oct): the tool's 52 px tile,
 * its full name and one line on what it's for; then work email, password, Sign in; "Staff only".
 */
import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { TOOL, TOOL_ICON, TOOL_NAME, TOOL_PURPOSE } from '@/lib/tool';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, error, isLoading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    try {
      await login({ email, password });
      navigate('/');
    } catch (error) {
      // Error is handled by auth store
      console.error('Login failed:', error);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-[400px] rounded-[18px] border border-border bg-card px-8 py-9 max-md:px-6">
        <div className="flex flex-col items-center text-center">
          <img src={TOOL_ICON[TOOL]} alt="" className="h-[52px] w-[52px]" />
          <h1 className="mt-5 text-[22px] font-bold tracking-[-0.015em] text-foreground">{TOOL_NAME[TOOL]}</h1>
          <p className="mt-1.5 text-[13.5px] text-muted-foreground">{TOOL_PURPOSE[TOOL]}</p>
        </div>

        <form className="mt-7 space-y-3" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email-address" className="sr-only">Work email</label>
            <input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="block h-11 w-full rounded-[10px] border border-border bg-background px-3.5 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-[#8b93ff] focus:outline-none"
              placeholder="Work email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="password" className="sr-only">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              className="block h-11 w-full rounded-[10px] border border-border bg-background px-3.5 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-[#8b93ff] focus:outline-none"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <p role="alert" className="rounded-[10px] bg-[#F87171]/[0.12] px-3.5 py-2.5 text-[13px] text-[#F87171]">{error}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="mt-1 flex h-11 w-full items-center justify-center rounded-full bg-[#F3F0EA] text-[14px] font-bold text-[#15151c] hover:bg-[#F3F0EA]/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? 'Signing in…' : 'Sign in'}
          </button>
          {/* Design's sketch says "Staff only · two-step sign-in"; two-step comes with Tools' work (5 to 10 Nov). */}
          <p className="pt-2 text-center text-[12px] text-muted-foreground">Staff only</p>

          {/* 2026-08-03 (Tools, Scoring [FIX] memo): removed the
              "admin@playbook.com / changeme" dev-creds hint. Those
              credentials stopped working months ago when CC auth moved
              to the learner table with a `permission_role == 'admin'`
              gate — the hint sent people down a dead end (Nino lost
              most of an evening to it). A default password printed on
              a login page is also a bad habit even when wrong, so the
              safe version is no element. Separately, a proper reset
              flow is on Product's queue (there's no in-UI recovery
              path today; `scripts/seed_cc_admin.py` in the scoring
              engine is the stopgap for lockouts). */}
        </form>
      </div>
    </div>
  );
}
